<template>
  <!--
    "Em transição" usa o azul institucional (#0f62fe) do design aprovado, que não
    existe na paleta do LabelBadge (cujo `blue` é o teal primário #2F7F91). Para
    não repintar o badge compartilhado, este estado replica a estrutura do
    LabelBadge com as cores exatas da especificação.
  -->
  <div
    v-if="isInTransition"
    data-testid="handover-status-badge"
    class="flex items-center gap-1 w-fit rounded border font-medium text-sm px-2.5 py-1 border-[#0f62fe] bg-[#edf5ff] text-[#16161d]"
  >
    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-[#0f62fe]" viewBox="0 0 24 24">
      <path :d="mdiSwapHorizontal" fill="currentColor" />
    </svg>
    {{ badgeLabel }}
  </div>

  <LabelBadge
    v-else
    data-testid="handover-status-badge"
    :label="badgeLabel"
    :color="badgeColor"
    :icon="badgeIcon"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiCheckCircle, mdiCloseCircle, mdiMinusCircle, mdiSwapHorizontal } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import type { LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import { PackageStatus } from '@/Network/Types/Requests/HandoverInviteRequests'

const props = defineProps<{
  status: PackageStatus
}>()

const isApproved = computed(() => props.status === PackageStatus.APPROVED)
// `?.` porque a listagem serve pacotes de várias origens: um status ausente
// derrubava a linha inteira com TypeError em vez de degradar o badge.
const isCancelled = computed(() => props.status?.startsWith('CANCELLED_BY_') ?? false)
// PENDING = convite enviado, médicos ainda não responderam. É o estado que a
// operação chama de "em transição" e ganha destaque azul próprio.
const isInTransition = computed(() => props.status === PackageStatus.PENDING)
const isPending = computed(
  () =>
    props.status === PackageStatus.AWAITING_RESPONSES ||
    props.status === PackageStatus.IN_ADMIN_QUEUE ||
    props.status === PackageStatus.DRAFT
)

const LABEL_MAP: Partial<Record<PackageStatus, string>> = {
  [PackageStatus.APPROVED]: 'Aprovado',
  [PackageStatus.EXPIRED]: 'Expirado',
  [PackageStatus.PENDING]: 'Em transição',
  [PackageStatus.AWAITING_RESPONSES]: 'Aguardando respostas',
  [PackageStatus.IN_ADMIN_QUEUE]: 'Em fila de aprovação',
  [PackageStatus.DRAFT]: 'Rascunho',
  // Quem encerrou a troca muda o que o operador precisa entender: o médico
  // desistiu da própria solicitação ("Cancelado"), enquanto os outros ramos são
  // o fluxo recusando a troca ("Reprovado"). Colapsar os quatro num rótulo só
  // escondia a diferença que a Operação usa para saber de quem cobrar.
  [PackageStatus.CANCELLED_BY_REQUESTER]: 'Cancelado',
  [PackageStatus.CANCELLED_BY_SYSTEM]: 'Reprovado',
  [PackageStatus.CANCELLED_BY_CASCADE]: 'Reprovado',
  [PackageStatus.CANCELLED_BY_STATION_EDIT]: 'Reprovado'
}

const badgeLabel = computed(() => {
  if (!props.status) return ''
  const mapped = LABEL_MAP[props.status]
  if (mapped) return mapped
  // Ramo CANCELLED_BY_* novo, ainda sem rótulo próprio: "Reprovado" erra menos
  // que despejar o valor cru do enum na tela.
  return isCancelled.value ? 'Reprovado' : props.status
})

const badgeColor = computed<LabelBadgeColor>(() => {
  if (isApproved.value) return 'green'
  if (isCancelled.value) return 'red'
  if (isPending.value) return 'yellow'
  return 'gray'
})

const badgeIcon = computed(() => {
  if (isApproved.value) return mdiCheckCircle
  if (isCancelled.value) return mdiCloseCircle
  return mdiMinusCircle
})
</script>
